import { CellUpdatePayload } from "@modules/spreadsheets/dto/spreadsheet/type";
import { SpreadsheetTableColumnId } from "../table.types";

/**
 * Types of actions that can be undone/redone
 */
export enum UndoRedoActionType {
  CELL_EDIT = "CELL_EDIT",
  ROW_ADD = "ROW_ADD",
  ROW_DELETE = "ROW_DELETE",
  CUT_PASTE = "CUT_PASTE",
  BULK_FILL = "BULK_FILL",
}

/**
 * Base interface for all undo/redo actions
 */
export interface BaseUndoRedoAction {
  id: string;
  type: UndoRedoActionType;
  timestamp: number;
  description: string;
}

/**
 * Cell edit action - stores the complete cell update information
 */
export interface CellEditAction extends BaseUndoRedoAction {
  type: UndoRedoActionType.CELL_EDIT;
  currentPayload: CellUpdatePayload;
  previousPayload: CellUpdatePayload;
  rowIndex: number;
  columnId: SpreadsheetTableColumnId;
}

/**
 * Action for row additions
 */
export interface RowAddAction extends BaseUndoRedoAction {
  type: UndoRedoActionType.ROW_ADD;
  rowIndex: number;
  columnId: SpreadsheetTableColumnId;
  globalId: number;
  tableId: number;
  rrn: number;
}

export type DeletedRowData = {
      fieldId: number;
      value: string | number;
      rrn: number;
      tableId: number;
      columnIndex: number;
  }

/**
 * Action for row deletions (supports both single and bulk operations)
 */
export interface RowDeleteAction extends BaseUndoRedoAction {
  type: UndoRedoActionType.ROW_DELETE;
  deletedRows: Array<{
    rowIndex: number;
    columnId: SpreadsheetTableColumnId;
    globalId: number;
    tableId: number;
    rrn: number;
    deletedRowData: Array<DeletedRowData>;
  }>; // Store all deleted rows with their data
}

/**
 * Action for bulk fill operations (fill up/down)
 */
export interface BulkFillAction extends BaseUndoRedoAction {
  type: UndoRedoActionType.BULK_FILL;
  fillOperations: Array<{
    rowIndex: number;
    columnId: SpreadsheetTableColumnId;
    newValue: string | number;
    originalValue: string | number;
    payload: CellUpdatePayload;
    columnIndex: number
  }>;
  fillDirection: 'up' | 'down';
}

/**
 * Union type for all possible actions
 */
export type UndoRedoAction = CellEditAction | RowAddAction | RowDeleteAction | BulkFillAction;

/**
 * Undo/Redo manager state
 */
export interface UndoRedoState {
  undoStack: UndoRedoAction[];
  redoStack: UndoRedoAction[];
  maxStackSize: number;
  isPerformingAction: boolean; // Prevents tracking during undo/redo operations
}

/**
 * Configuration for undo/redo system
 */
export interface UndoRedoConfig {
  maxStackSize?: number;
  debounceMs?: number;
}

/**
 * Callback functions for undo/redo operations
 */
export interface UndoRedoCallbacks {
  onCellEdit: (
    payload: CellUpdatePayload,
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    value: string | number
  ) => Promise<void>;
  onRowAdd: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    globalId: number,
    tableId: number,
    rrn: number
  ) => Promise<{ rrn: number }>;
  onRowDelete: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    globalId: number,
    tableId: number,
    rrn: number
  ) => Promise<void>;
  onBulkCellUpdate?: (
    updates: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      value: string | number;
      payload: CellUpdatePayload;
      columnIndex: number
    }>
  ) => Promise<void>;
  
  // NEW: Bulk operation callbacks that don't update state immediately
  onRowAddWithoutStateUpdate?: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    globalId: number,
    tableId: number,
    rrn: number
  ) => Promise<{ rrn: number; response: any }>;
  
  onBulkCellUpdateWithoutStateUpdate?: (
    updates: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      value: string | number;
      payload: CellUpdatePayload;
    }>
  ) => Promise<void>;
  
  onBulkStateUpdate?: (bulkUpdateData: {
    rowAdditions: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      globalId: number;
      tableId: number;
      rrn: number;
      response: any;
    }>;
    cellUpdates: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      value: string | number;
      payload: CellUpdatePayload;
      columnIndex: number
    }>;
    totalCountChange: number;
  }) => Promise<void>;
  
  getFieldInfo?: (fieldId: number) => { system?: boolean } | undefined;
  getCurrentRowData?: () => Array<{ rrn: number; rowIndex: number; [key: string]: any }> | undefined;
  
  // NEW: Redo operation callbacks that don't update state immediately
  onRowDeleteWithoutStateUpdate?: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    globalId: number,
    tableId: number,
    rrn: number
  ) => Promise<void>;
  
  onBulkStateUpdateForRedo?: (bulkUpdateData: {
    rowDeletions: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      globalId: number;
      tableId: number;
      rrn: number;
    }>;
    totalCountChange: number;
  }) => Promise<void>;
  
  onError?: (error: string) => void;
}
